/**
 * CommunityCard Component
 * Card for displaying a community in grids
 * Used in the Communities tab and other community listings
 */

import { BookmarkFilledIcon, BookmarkIcon, LockIcon } from "@components/icons/index"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { CommunityCard as CommunityCardType } from "@/features/communities/types/index"
import { useToggleCommunitySave } from "@/features/profile/hooks/useSavedCommunities"
import { cn } from "@/lib"

interface CommunityCardProps {
  community: CommunityCardType
  className?: string
}

export function CommunityCard({ community, className }: CommunityCardProps) {
  const navigate = useNavigate()
  const toggleSave = useToggleCommunitySave()
  const isGuest = !useAuthStore(state => state.user)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Track locally-toggled IDs so we can show the icon flip instantly
  // without invalidating queries (which could reorder lists).
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set())

  // Reset local state when the card switches to a different community
  // biome-ignore lint/correctness/useExhaustiveDependencies: community.id is intentionally watched for reset
  useEffect(() => {
    setToggledIds(new Set())
  }, [community.id])

  const isSaved =
    community.isSaved !== undefined
      ? toggledIds.has(community.id)
        ? !community.isSaved
        : community.isSaved
      : false

  const handleBookmark = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (isGuest) {
        setShowLoginModal(true)
        return
      }

      // Optimistic flip
      setToggledIds(prev => {
        const next = new Set(prev)
        if (next.has(community.id)) {
          next.delete(community.id)
        } else {
          next.add(community.id)
        }
        return next
      })

      toggleSave.mutate(community.id, {
        onError: () => {
          setToggledIds(prev => {
            const next = new Set(prev)
            next.delete(community.id)
            return next
          })
        },
      })
    },
    [community.id, isGuest, toggleSave],
  )

  const handleClick = () => {
    navigate(`/communities/${community.id}`)
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative bg-card rounded-xl overflow-hidden flex flex-col shadow-sm cursor-pointer hover:opacity-90 transition-opacity",
        className,
      )}
    >
      {/* Image Placeholder */}
      <div className="aspect-[16/11] bg-brand-beige overflow-hidden">
        {community.coverImage ? (
          <img
            src={community.coverImage}
            alt={community.name}
            className="w-full h-full object-fill"
          />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={handleBookmark}
        className={cn(
          "absolute top-5 right-5 z-10 shrink-0 cursor-pointer rounded-[10px] p-2.75 transition-colors",
          " rounded-[10px] shadow-[0_5px_10px_rgba(2,2,2,0.30)] backdrop-blur-[10px] border-gradient-glass",
        )}
        aria-label={isSaved ? "Unsave community" : "Save community"}
      >
        {isSaved ? (
          <BookmarkFilledIcon className="w-[22px] h-[22px] fill-white text-white" />
        ) : (
          <BookmarkIcon className="w-[22px] h-[22px] text-primary" />
        )}
      </button>

      {/* Content */}
      <div className="absolute bottom-0 px-4 py-6 w-full max-h-[90px] md:max-h-[130px] bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-[rgba(0,0,0,0)]">
        <h3 className="section-heading text-sm md:text-[18px] leading-[22px] tracking-normal text-white mb-1">
          {community.name}
          {community.type === "PRIVATE" && <LockIcon className="inline ml-2 w-5 h-5 mb-1" />}
        </h3>
        <p className="text-white text-[12px] md:text-[14px] my-1">
          {community.memberCount} members &nbsp; &nbsp; {community.ritualCount} Rituals
        </p>
        {community.city && community.state && (
          <p className="font-normal text-[12px] md:text-[14px] leading-[100%] tracking-normal text-white md:my-1">
            {community.city}, {community.state}
          </p>
        )}
      </div>

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
